import * as THREE from 'three';

/**
 * ULTRA PERFORMANS: Temporal LOD & Progressive Rendering
 * Frame rate'e göre adaptif render kalitesi
 *
 * Features:
 * - Per-object render frequency based on importance
 * - Distance-based temporal LOD
 * - Motion-aware rendering (moving = low quality)
 * - Background rendering at lower FPS
 * - Reprojection for skipped frames
 *
 * Benefits:
 * - Stable 60 FPS even with complex scenes
 * - Perceptually smooth despite lower actual renders
 * - Intelligent GPU budget allocation
 * - Reduced power consumption
 */

interface TemporalObject {
  object: THREE.Object3D;
  importance: number;           // 0-1, higher = more important
  lastRenderFrame: number;      // Frame when last rendered
  renderFrequency: number;      // Render every N frames
  distance: number;             // Distance from camera
  screenSize: number;           // Screen-space size in pixels
  isMoving: boolean;            // Was moved this frame
  velocity: THREE.Vector3;      // For reprojection
  previousMatrix: THREE.Matrix4;
}

interface RenderTier {
  name: string;
  frequencyDivisor: number;     // 1 = every frame, 2 = every 2 frames
  qualityMultiplier: number;    // 1 = full, 0.5 = half resolution
  maxObjects: number;           // Max objects in this tier
}

// Pre-allocated
const _tempVec3 = new THREE.Vector3();
const _tempMatrix = new THREE.Matrix4();
const _tempBox = new THREE.Box3();
const _tempSphere = new THREE.Sphere();

export class FikoTemporalLOD {
  private camera: THREE.Camera;
  private scene: THREE.Scene;
  private renderer: THREE.WebGLRenderer;

  // Tracked objects
  private objects = new Map<string, TemporalObject>();

  // Render tiers
  private tiers: RenderTier[] = [
    { name: 'critical', frequencyDivisor: 1, qualityMultiplier: 1.0, maxObjects: 50 },
    { name: 'high', frequencyDivisor: 1, qualityMultiplier: 1.0, maxObjects: 200 },
    { name: 'medium', frequencyDivisor: 2, qualityMultiplier: 0.75, maxObjects: 500 },
    { name: 'low', frequencyDivisor: 3, qualityMultiplier: 0.5, maxObjects: 1000 },
    { name: 'background', frequencyDivisor: 4, qualityMultiplier: 0.25, maxObjects: Infinity }
  ];

  // Frame tracking
  private frameCount = 0;
  private lastFrameTime = 0;
  private frameTimeHistory: number[] = [];
  private targetFrameTime = 16.67; // 60fps

  // Configuration
  private config = {
    enabled: true,
    minImportance: 0.1,
    distanceThresholds: [10, 50, 100, 200], // meters
    screenSizeThresholds: [100, 50, 25, 10], // pixels
    motionPenalty: 0.5, // Reduce frequency while moving
    reprojectionEnabled: true,
    adaptiveEnabled: true
  };

  // Reprojection buffers
  private previousFrameTexture: THREE.WebGLRenderTarget | null = null;
  private reprojectionMaterial: THREE.ShaderMaterial | null = null;

  // Stats
  private stats = {
    totalObjects: 0,
    objectsRendered: 0,
    objectsSkipped: 0,
    framesReprojected: 0,
    averageFrameTime: 0,
    currentTierDistribution: {} as Record<string, number>
  };

  constructor(
    camera: THREE.Camera,
    scene: THREE.Scene,
    renderer: THREE.WebGLRenderer
  ) {
    this.camera = camera;
    this.scene = scene;
    this.renderer = renderer;

    this.initReprojection();
  }

  /**
   * Initialize reprojection system
   */
  private initReprojection(): void {
    const size = this.renderer.getSize(new THREE.Vector2());

    this.previousFrameTexture = new THREE.WebGLRenderTarget(
      size.x, size.y,
      {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType
      }
    );

    this.reprojectionMaterial = new THREE.ShaderMaterial({
      uniforms: {
        tPreviousFrame: { value: null },
        uVelocity: { value: new THREE.Vector2() },
        uBlendFactor: { value: 0.5 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tPreviousFrame;
        uniform vec2 uVelocity;
        uniform float uBlendFactor;
        varying vec2 vUv;

        void main() {
          // Sample previous frame with motion offset
          vec2 prevUv = vUv - uVelocity;
          vec4 prevColor = texture2D(tPreviousFrame, prevUv);

          gl_FragColor = prevColor;
        }
      `
    });
  }

  /**
   * Register object for temporal LOD
   */
  registerObject(object: THREE.Object3D, importance: number = 0.5): void {
    const id = object.uuid;

    this.objects.set(id, {
      object,
      importance: Math.max(0, Math.min(1, importance)),
      lastRenderFrame: 0,
      renderFrequency: 1,
      distance: 0,
      screenSize: 100,
      isMoving: false,
      velocity: new THREE.Vector3(),
      previousMatrix: object.matrixWorld.clone()
    });

    this.stats.totalObjects++;
  }

  /**
   * Unregister object
   */
  unregisterObject(object: THREE.Object3D): void {
    if (this.objects.delete(object.uuid)) {
      this.stats.totalObjects--;
    }
  }

  /**
   * Update object importance dynamically
   */
  setImportance(object: THREE.Object3D, importance: number): void {
    const tracked = this.objects.get(object.uuid);
    if (tracked) {
      tracked.importance = Math.max(0, Math.min(1, importance));
    }
  }

  /**
   * Update all objects before render
   * Returns list of objects that should be rendered this frame
   */
  update(): THREE.Object3D[] {
    if (!this.config.enabled) {
      return Array.from(this.objects.values()).map(o => o.object);
    }

    const frameStart = performance.now();
    this.frameCount++;

    // Update frame time tracking
    if (this.lastFrameTime > 0) {
      const dt = frameStart - this.lastFrameTime;
      this.frameTimeHistory.push(dt);
      if (this.frameTimeHistory.length > 30) {
        this.frameTimeHistory.shift();
      }
      this.stats.averageFrameTime = this.frameTimeHistory.reduce((a, b) => a + b) /
        this.frameTimeHistory.length;
    }
    this.lastFrameTime = frameStart;

    // Adaptive: adjust tier frequencies based on frame time
    if (this.config.adaptiveEnabled) {
      this.adaptTierFrequencies();
    }

    // Update all objects
    const objectsToRender: THREE.Object3D[] = [];
    const tierCounts: Record<string, number> = {};

    this.objects.forEach((tracked, id) => {
      // Update distance and screen size
      this.updateObjectMetrics(tracked);

      // Detect motion
      this.detectMotion(tracked);

      // Assign tier based on metrics
      const tier = this.assignTier(tracked);
      tierCounts[tier.name] = (tierCounts[tier.name] || 0) + 1;

      // Update render frequency
      tracked.renderFrequency = tier.frequencyDivisor;

      // Check if should render this frame
      const framesSinceRender = this.frameCount - tracked.lastRenderFrame;
      const shouldRender = framesSinceRender >= tracked.renderFrequency;

      if (shouldRender) {
        tracked.lastRenderFrame = this.frameCount;
        tracked.object.visible = true;
        objectsToRender.push(tracked.object);
        this.stats.objectsRendered++;
      } else {
        // Apply reprojection for skipped object
        if (this.config.reprojectionEnabled) {
          this.applyReprojection(tracked);
        } else {
          tracked.object.visible = false;
        }
        this.stats.objectsSkipped++;
      }

      // Store matrix for next frame
      tracked.previousMatrix.copy(tracked.object.matrixWorld);
    });

    this.stats.currentTierDistribution = tierCounts;

    return objectsToRender;
  }

  /**
   * Update distance and screen size for object
   */
  private updateObjectMetrics(tracked: TemporalObject): void {
    const obj = tracked.object;

    // Get world position
    obj.getWorldPosition(_tempVec3);

    // Distance from camera
    tracked.distance = _tempVec3.distanceTo(this.camera.position);

    // Calculate screen size
    if (obj instanceof THREE.Mesh) {
      if (!obj.geometry.boundingSphere) {
        obj.geometry.computeBoundingSphere();
      }

      const sphere = obj.geometry.boundingSphere!;
      const worldRadius = sphere.radius * Math.max(
        obj.scale.x, obj.scale.y, obj.scale.z
      );

      // Approximate screen size
      const fov = (this.camera as THREE.PerspectiveCamera).fov || 75;
      const height = this.renderer.getSize(new THREE.Vector2()).y;
      const angularSize = 2 * Math.atan(worldRadius / tracked.distance);
      tracked.screenSize = (angularSize / (fov * Math.PI / 180)) * height;
    }
  }

  /**
   * Detect if object is moving
   */
  private detectMotion(tracked: TemporalObject): void {
    const currentMatrix = tracked.object.matrixWorld;
    const prevMatrix = tracked.previousMatrix;

    // Check if matrices differ
    let moved = false;
    for (let i = 0; i < 16; i++) {
      if (Math.abs(currentMatrix.elements[i] - prevMatrix.elements[i]) > 0.0001) {
        moved = true;
        break;
      }
    }

    tracked.isMoving = moved;

    if (moved) {
      // Calculate velocity
      _tempVec3.setFromMatrixPosition(currentMatrix);
      const prevPos = new THREE.Vector3().setFromMatrixPosition(prevMatrix);
      tracked.velocity.subVectors(_tempVec3, prevPos);
    } else {
      tracked.velocity.set(0, 0, 0);
    }
  }

  /**
   * Assign render tier based on object metrics
   */
  private assignTier(tracked: TemporalObject): RenderTier {
    // Calculate combined score
    let score = tracked.importance;

    // Distance factor (closer = more important)
    const distThresholds = this.config.distanceThresholds;
    if (tracked.distance < distThresholds[0]) {
      score *= 1.0;
    } else if (tracked.distance < distThresholds[1]) {
      score *= 0.8;
    } else if (tracked.distance < distThresholds[2]) {
      score *= 0.5;
    } else if (tracked.distance < distThresholds[3]) {
      score *= 0.25;
    } else {
      score *= 0.1;
    }

    // Screen size factor
    const sizeThresholds = this.config.screenSizeThresholds;
    if (tracked.screenSize > sizeThresholds[0]) {
      score *= 1.0;
    } else if (tracked.screenSize > sizeThresholds[1]) {
      score *= 0.8;
    } else if (tracked.screenSize > sizeThresholds[2]) {
      score *= 0.5;
    } else if (tracked.screenSize > sizeThresholds[3]) {
      score *= 0.25;
    } else {
      score *= 0.1;
    }

    // Motion penalty (moving objects need more updates, but less quality)
    if (tracked.isMoving) {
      score *= (1 - this.config.motionPenalty);
    }

    // Map score to tier
    if (score > 0.8) return this.tiers[0];      // critical
    if (score > 0.6) return this.tiers[1];      // high
    if (score > 0.4) return this.tiers[2];      // medium
    if (score > 0.2) return this.tiers[3];      // low
    return this.tiers[4];                        // background
  }

  /**
   * Adapt tier frequencies based on frame time
   */
  private adaptTierFrequencies(): void {
    const avgFrameTime = this.stats.averageFrameTime;

    if (avgFrameTime > this.targetFrameTime * 1.5) {
      // Heavily over budget - increase all frequencies
      this.tiers.forEach(tier => {
        tier.frequencyDivisor = Math.min(8, tier.frequencyDivisor + 1);
      });
    } else if (avgFrameTime > this.targetFrameTime * 1.2) {
      // Over budget - increase lower tier frequencies
      this.tiers[2].frequencyDivisor = Math.min(4, this.tiers[2].frequencyDivisor + 1);
      this.tiers[3].frequencyDivisor = Math.min(6, this.tiers[3].frequencyDivisor + 1);
      this.tiers[4].frequencyDivisor = Math.min(8, this.tiers[4].frequencyDivisor + 1);
    } else if (avgFrameTime < this.targetFrameTime * 0.8) {
      // Under budget - can reduce frequencies (more updates)
      this.tiers.forEach(tier => {
        tier.frequencyDivisor = Math.max(1, tier.frequencyDivisor - 1);
      });
    }
  }

  /**
   * Apply reprojection to skipped object
   */
  private applyReprojection(tracked: TemporalObject): void {
    // For now, just keep object visible with last state
    // Full reprojection would use motion vectors
    tracked.object.visible = true;
    this.stats.framesReprojected++;
  }

  /**
   * Store current frame for reprojection
   */
  storeFrame(): void {
    if (!this.previousFrameTexture) return;

    const currentTarget = this.renderer.getRenderTarget();
    this.renderer.setRenderTarget(this.previousFrameTexture);
    this.renderer.render(this.scene, this.camera);
    this.renderer.setRenderTarget(currentTarget);
  }

  /**
   * Get statistics
   */
  getStats(): {
    totalObjects: number;
    objectsRendered: number;
    objectsSkipped: number;
    framesReprojected: number;
    averageFrameTime: string;
    estimatedFPS: number;
    tierDistribution: Record<string, number>;
    renderReduction: string;
  } {
    const estimatedFPS = this.stats.averageFrameTime > 0
      ? 1000 / this.stats.averageFrameTime
      : 60;

    const total = this.stats.objectsRendered + this.stats.objectsSkipped;
    const reduction = total > 0
      ? ((this.stats.objectsSkipped / total) * 100).toFixed(1)
      : '0';

    return {
      totalObjects: this.stats.totalObjects,
      objectsRendered: this.stats.objectsRendered,
      objectsSkipped: this.stats.objectsSkipped,
      framesReprojected: this.stats.framesReprojected,
      averageFrameTime: `${this.stats.averageFrameTime.toFixed(2)}ms`,
      estimatedFPS: Math.round(estimatedFPS),
      tierDistribution: this.stats.currentTierDistribution,
      renderReduction: `${reduction}%`
    };
  }

  /**
   * Reset frame stats
   */
  resetFrameStats(): void {
    this.stats.objectsRendered = 0;
    this.stats.objectsSkipped = 0;
  }

  /**
   * Configure system
   */
  configure(config: Partial<typeof this.config>): void {
    Object.assign(this.config, config);
  }

  /**
   * Resize reprojection buffers
   */
  resize(width: number, height: number): void {
    if (this.previousFrameTexture) {
      this.previousFrameTexture.setSize(width, height);
    }
  }

  /**
   * Cleanup
   */
  dispose(): void {
    this.objects.clear();

    if (this.previousFrameTexture) {
      this.previousFrameTexture.dispose();
    }

    if (this.reprojectionMaterial) {
      this.reprojectionMaterial.dispose();
    }
  }
}

/**
 * ULTRA PERFORMANS: Progressive Scene Loader
 * Load scene progressively based on importance
 */
export class FikoProgressiveSceneLoader {
  private scene: THREE.Scene;
  private camera: THREE.Camera;

  private loadQueue: Array<{
    object: THREE.Object3D;
    priority: number;
    loaded: boolean;
  }> = [];

  private loadBudgetMs = 5;
  private isLoading = false;

  constructor(scene: THREE.Scene, camera: THREE.Camera) {
    this.scene = scene;
    this.camera = camera;
  }

  /**
   * Queue object for progressive loading
   */
  queueObject(object: THREE.Object3D, priority: number = 0.5): void {
    this.loadQueue.push({
      object,
      priority,
      loaded: false
    });

    // Sort by priority
    this.loadQueue.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Process load queue within budget
   */
  processQueue(): { loaded: number; remaining: number } {
    if (this.isLoading || this.loadQueue.length === 0) {
      return { loaded: 0, remaining: this.loadQueue.length };
    }

    this.isLoading = true;
    const startTime = performance.now();
    let loaded = 0;

    while (
      this.loadQueue.length > 0 &&
      performance.now() - startTime < this.loadBudgetMs
    ) {
      const item = this.loadQueue[0];

      if (!item.loaded) {
        this.scene.add(item.object);
        item.loaded = true;
        loaded++;
      }

      this.loadQueue.shift();
    }

    this.isLoading = false;

    return {
      loaded,
      remaining: this.loadQueue.length
    };
  }

  /**
   * Get queue status
   */
  getStatus(): { queueLength: number; isLoading: boolean } {
    return {
      queueLength: this.loadQueue.length,
      isLoading: this.isLoading
    };
  }

  /**
   * Clear queue
   */
  clearQueue(): void {
    this.loadQueue = [];
  }
}
